import { DropdownMenuItem, DropdownMenuLabel } from '@heidi/design-system/dropdown-menu';
import { Evidence, Routines } from '@heidi/design-system/icons';
import { Kbd } from '@heidi/design-system/kbd';
import { AudioLines, Blocks, FileText, SquareCheck } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

export interface CreateMenuItemsProps {
  headerLabel: string;
  sessionLabel: string;
  chatLabel: string;
  taskLabel: string;
  templateLabel: string;
  pdfFormLabel: string;
  sessionAriaLabel?: string;
  isSessionDisabled: boolean;
  /** Hide the Session item entirely (EHR-launch lock). */
  isSessionHidden?: boolean;
  isEvidenceEnabled: boolean;
  isTasksEnabled: boolean;
  isWorkflowsEnabled: boolean;
  isOfflineGated: boolean;
  onNewSession: () => void;
  onNewEvidenceChat: () => void;
  onNewTask: () => void;
  onNewRoutine: () => void;
  onNewTemplate: () => void;
  onNewPdfForm: () => void;
}

export function CreateMenuItems({
  headerLabel,
  sessionLabel,
  chatLabel,
  taskLabel,
  templateLabel,
  pdfFormLabel,
  sessionAriaLabel,
  isSessionDisabled,
  isSessionHidden = false,
  isEvidenceEnabled,
  isTasksEnabled,
  isWorkflowsEnabled,
  isOfflineGated,
  onNewSession,
  onNewEvidenceChat,
  onNewTask,
  onNewRoutine,
  onNewTemplate,
  onNewPdfForm,
}: CreateMenuItemsProps) {
  return (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-text-tertiary">
        {headerLabel}
      </DropdownMenuLabel>

      {!isSessionHidden && (
        <DropdownMenuItem
          onClick={onNewSession}
          disabled={isSessionDisabled}
          data-testid="global-create-new-session"
          aria-label={sessionAriaLabel}
        >
          <AudioLines />
          {sessionLabel}
          <Kbd size="compact" className="ms-auto">
            N
          </Kbd>
        </DropdownMenuItem>
      )}

      {isEvidenceEnabled && (
        <DropdownMenuItem
          onClick={onNewEvidenceChat}
          disabled={isOfflineGated}
          data-testid="global-create-new-evidence-chat"
        >
          <Evidence />
          {chatLabel}
        </DropdownMenuItem>
      )}

      {isTasksEnabled && (
        <DropdownMenuItem
          onClick={onNewTask}
          disabled={isOfflineGated}
          data-testid="global-create-new-task"
        >
          <SquareCheck />
          {taskLabel}
        </DropdownMenuItem>
      )}

      {isWorkflowsEnabled && (
        <DropdownMenuItem
          onClick={onNewRoutine}
          disabled={isOfflineGated}
          data-testid="global-create-new-routine"
        >
          <Routines />
          <FormattedMessage
            defaultMessage="Routine"
            id="Ypv912jKZs"
            description="Sidenav universal create menu item that starts routine setup with Heidi"
          />
        </DropdownMenuItem>
      )}

      <DropdownMenuItem
        onClick={onNewTemplate}
        disabled={isOfflineGated}
        data-testid="global-create-new-template"
      >
        <Blocks />
        {templateLabel}
      </DropdownMenuItem>

      {/* Opens the form-template upload modal, which narrows itself to XML
          on-prem. The label is chosen by the caller for the same reason —
          "PDF form" would name a format the appliance cannot accept. */}
      <DropdownMenuItem
        onClick={onNewPdfForm}
        disabled={isOfflineGated}
        data-testid="global-create-new-pdf-form"
      >
        <FileText />
        {pdfFormLabel}
      </DropdownMenuItem>
    </>
  );
}
